import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { first, tap } from 'rxjs/operators';

import { ScrollingService } from 'src/app/core/services/scrolling/scrolling.service';
import { fromMq } from 'src/app/store/common/common.selectors';
import { financingUpdateIsFcisWidgetOpen } from 'src/app/store/financing/financing.actions';
import { selectIsFcisInitializedAndWidgetOpen } from 'src/app/store/financing/financing.selectors';

import { BaseModal } from '../../modal/modal-host/base-modal';
import { ModalService } from '../../modal/modal-service/modal.service';

@Component({
  selector: 'cc-finance-calculator-modal',
  templateUrl: './finance-calculator-modal.component.html',
  // TODO align animation with modal.component [ticket OWCC-499]
  animations: [
    trigger('showHideWidget', [
      state('false', style({ opacity: 0, transform: 'scale(0)' })),
      transition('0 => 1', animate('.3s linear', style({ opacity: 1 }))),
      transition('0 => 1', animate('.3s .01s linear', style({ transform: 'scale(1)' }))),
      transition('1 => 0', animate('.3s linear', style({ opacity: 0 }))),
      transition('1 => 0', animate('.3s .01s linear', style({ transform: 'scale(0)' })))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // TODO: make this a standalone component
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false
})
export class FinanceCalculatorModalComponent extends BaseModal {
  public displayWidgetContent = false;
  public readonly displayWidget$: Observable<boolean> = this.store.select(selectIsFcisInitializedAndWidgetOpen).pipe(
    tap(isWidgetInitializedAndOpen => {
      if (isWidgetInitializedAndOpen) {
        this.displayWidgetContent = true;
      } else {
        // MBCC-1117: We still use javascript for setting scroll-lock in the finance modal, because setting scroll-lock in the template is not possible.
        // Because the finance modal is rendered at any time and thus disables the complete scrolling of the page.
        this.scrollingService.enableScrolling();
        this.scrollingService.enableScrollingMobile();
      }
      this.changeDetectorRef.markForCheck();
    })
  );

  public readonly isMobile$ = this.store.select(fromMq.selectIsMq2Max).pipe(takeUntilDestroyed());

  public constructor(
    modalService: ModalService,
    scrollingService: ScrollingService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store
  ) {
    super(modalService, scrollingService);
  }

  public override onClose(): void {
    this.store.dispatch(financingUpdateIsFcisWidgetOpen({ isFcisWidgetOpen: false }));
    super.onClose();
  }

  public onAnimationDone(event: AnimationEvent): void {
    /* in general the widget itself and its content shall be displayed/not displayed at the same times.
    But to ensure the animation works properly we cannot set the display:none property at the exact same time as the hiding of the widget.
    Therefore we synchronize the visibility of the widget and the widget content after the animation */
    this.displayWidget$.pipe(first()).subscribe(display => {
      this.displayWidgetContent = display;
    });

    // event is fired when clicking on 'motorization' (in this case event.toState is 'void').
    // click on motorization must not block scrolling.
    // TODO: this was a hotfix! see OWCC-1317 - please check OWCC-499 && OWCC-1319
    this.isMobile$.pipe(first()).subscribe(isMobile => {
      const isTriggeredByUserInteraction = !event.fromState && event.toState !== 'void';
      if (isTriggeredByUserInteraction) {
        if (isMobile) {
          this.scrollingService.disableScrollingMobile();
        } else {
          this.scrollingService.disableScrolling();
        }
      }
    });
  }
}
